<template>
  <div
    :class="`sidebar-resume sidebar-resume--${
      contained ? 'contained' : 'alone'
    }`"
  >
    <!--<h3 class="h3">Riepilogo tariffa</h3>-->
    <div v-for="(flight_offer, index) in flight_offers" :key="'price_' + index">
      <h5 class="vb-heading">La tua scelta</h5>
      <div class="sidebar-resume__blocks">
        <!-- base -->
        <resume-block
          title="Tariffa:"
          :title_content="{
            icon: {
              name: 'flight_ticket',
              size: 16,
              color: sass('primary'),
            },
            text: branded_fare(flight_offer),
          }"
        >
          <template #default>
            <resume-row
              v-for="(group, age) in travelers_by_age(flight_offer)"
              :key="`group:${age}`"
              :label="`${group.list.length} ${transcode_traveler_type(
                age,
                group.list.length,
              )}`"
              :content="group.total"
              :currency_verbose="flight_offer.price.currency"
            />
            <resume-row
              label="Tasse e spese"
              :content="get_total_tax(flight_offer)"
              :currency_verbose="flight_offer.price.currency"
              info="<b>Perché una commissione di servizio?</b> Questa cifra ci permette di offrire e mantenere un'integrazione diretta con il tuo Welfare Provider e un'assistenza da record in caso di necessità. È l'unico costo che applichiamo per garantirti un processo di prenotazione fluido e sicuro."
            />
          </template>
          <template #footer>
            <resume-row
              label="Totale"
              classes="semibold"
              :content="get_base_total(flight_offer)"
              :currency_verbose="flight_offer.price.currency"
            />
          </template>
        </resume-block>

        <!-- included bags -->
        <resume-block
          title="Bagagli inclusi nella tariffa"
          info="I bagagli indicati si riferiscono al totale per tutti i passeggeri e tratte. Verifica sul sito della compagnia peso, misure e condizioni esatte."
        >
          <template #default>
            <resume-row
              label="Bagaglio personale"
              :content="flight_offer.travelerPricings.length"
              icon="backpack"
              info="È un piccolo bagaglio personale, come uno zaino o una borsa, da tenere sotto il sedile."
            />
            <resume-row
              v-if="
                flight_offer_has_upsell && branded_fare(flight_offer).length
              "
              label="Bagaglio a mano"
              :content="
                flight_offer.travelerPricings.length *
                (flight_offer.source === 'GDS' ? 1 : 0)
              "
              icon="trolley"
            >
              <template #content>
                <vb-icon
                  name="info"
                  :size="16"
                  :color="'#AAA'"
                  v-b-popover.hover.top="
                    `Controlla nella descrizione della tariffa ${
                      branded_fare(flight_offer) || 'che hai selezionato'
                    } quanti bagagli a mano sono inclusi.`
                  "
                >
                </vb-icon>
              </template>
            </resume-row>
            <resume-row
              label="Bagaglio da stiva"
              :content="get_all_included_bags(flight_offer)"
              icon="luggage"
            />
          </template>
        </resume-block>
      </div>
      <div
        v-if="get_paid_additional_services(flight_offer).length > 0"
        class="d-flex flex-column"
        style="gap: 30px; padding-top: 30px"
      >
        <div
          v-for="additional_service in get_paid_additional_services(
            flight_offer,
          )"
          :key="`additional_service:${additional_service.type}`"
        >
          <resume-block
            :title="
              capitalize_string(
                transcode_additional_services(additional_service.type, 99),
              )
            "
            :info="
              capitalize_string(
                get_additional_service_tooltip(additional_service.type),
              )
            "
          >
            <template
              v-if="additional_service.type === 'CHECKED_BAGS'"
              #default
            >
              <resume-row
                v-for="[key, extra_bag] in Object.entries(flexible_bags)"
                :key="key"
                :label="`Bagagli${
                  extra_bag.quantity * extra_bag.travelers.length === 1
                    ? 'o'
                    : ''
                } in stiva`"
                :content="extra_bag.quantity * extra_bag.travelers.length"
                icon="luggage"
              />
            </template>
            <template v-else-if="additional_service.type === 'SEATS'" #default>
              <resume-row
                v-for="(seats, key) in seats_per_segment(
                  flight_offer,
                  traveler_details,
                )"
                :key="`seats:${key}`"
                :label="formatSegmentLabel(segmentsMap[key])"
                icon="seats_choice"
              >
                <template #content>
                  <span
                    v-for="seat in seats"
                    :key="`seat:${key}:${seat.chargeableSeatNumber}`"
                    v-b-popover.hover.top="
                      seat.traveler_name || `P${seat.travelerId}`
                    "
                    class="text-primary ml-1"
                  >
                    {{ seat.chargeableSeatNumber }}
                  </span>
                </template>
              </resume-row>
            </template>
            <template v-if="Number(additional_service.amount)" #footer>
              <resume-row
                label="Totale"
                classes="semibold"
                :content="
                  Number(
                    apply_markup_to_number(
                      flight_offer,
                      additional_service.amount,
                    ),
                  )
                "
                :currency_verbose="flight_offer.price.currency"
              />
            </template>
          </resume-block>
        </div>
      </div>

      <template v-if="!contained">
        <div id="resume-total" class="mt-5 d-flex justify-content-between">
          <h3 class="h3 mb-0">Totale</h3>
          <h3 class="h3 mb-0">
            {{ currency(flight_offer.price.currency) }}
            {{ formatPrice(get_markup(flight_offer).mu_price) }}
          </h3>
        </div>

        <div>
          <p class="mb-2">
            Tariffa, tasse e spese amministrative per tutti i partecipanti
          </p>
        </div>

        <div v-if="post_sell_notice.text">
          <p>
            {{ post_sell_notice.text }}
          </p>
        </div>
      </template>
    </div>
  </div>
</template>

<script>
import {
  get_markup,
  apply_markup_to_number,
  transcode_traveler_type,
  parse_fligth_offer_bags,
  transcode_additional_services,
  get_additional_service_tooltip,
  get_paid_additional_services,
  seats_per_segment,
} from "@liveflight/utilities/flight-offer";
import { taxes } from "@liveflight/resources/taxes";
import { getSegmentsFromFlightOffers } from "@liveflight/utilities/seat-map";
import {
  formatPrice,
  is_infant,
  capitalize,
  currency,
  formatSegmentLabel,
} from "@liveflight/utilities/mixins";
import { number, subtract } from "mathjs";
import SidebarResumeBlock from "@liveflight/components/blocks/resume/resume-block.vue";
import SidebarRow from "@liveflight/components/blocks/resume/resume-row.vue";

export default {
  name: "resume",
  components: {
    "resume-block": SidebarResumeBlock,
    "resume-row": SidebarRow,
  },
  methods: {
    transcode_additional_services,
    transcode_traveler_type,
    formatPrice,
    apply_markup_to_number,
    is_infant,
    currency,
    formatSegmentLabel,
    get_additional_service_tooltip,
    get_paid_additional_services,
    seats_per_segment,
    parse_fligth_offer_bags,
    capitalize_string: capitalize,
    get_all_included_bags(flight_offer) {
      return this.parse_fligth_offer_bags(flight_offer, 0).included;
    },
    travelers_by_age(flight_offer) {
      return flight_offer.travelerPricings.reduce((groups, pricing) => {
        const previous = groups[pricing.travelerType] || { list: [], total: 0 };
        return {
          ...groups,
          [pricing.travelerType]: {
            list: [...previous.list, pricing],
            total: previous.total + number((pricing.price || {}).total || 0),
          },
        };
      }, {});
    },
    get_total_tax(flight_offer) {
      const tax = this.get_markup(flight_offer);
      return subtract(tax.mu_amount, tax.additional_services_mu_amount);
    },
    get_travelers_total(flight_offer) {
      return Object.values(this.travelers_by_age(flight_offer)).reduce(
        (total, elem) => total + elem.total,
        0,
      );
    },
    get_base_total(flight_offer) {
      return (
        this.get_travelers_total(flight_offer) +
        this.get_total_tax(flight_offer)
      );
    },
    branded_fare(flight_offer) {
      try {
        return this.capitalize_string(
          flight_offer.travelerPricings[0].fareDetailsBySegment[0].brandedFare,
        );
      } catch {
        return "";
      }
    },
  },
  data() {
    return {
      segmentsMap: {},
    };
  },
  props: {
    flight_offers: {
      type: Array,
      default: () => {
        return [];
      },
    },
    post_sell_notice: {
      type: Object,
      default: () => {
        return {};
      },
    },
    contained: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    flight_offer_has_upsell() {
      try {
        return this.$store.state.liveflight.flight_offer_has_upsell;
      } catch {
        return false;
      }
    },
    flexible_bags() {
      const flexible_bags = this.$store.state.liveflight.flexible_bags;

      const mapped = {};
      Object.entries(flexible_bags).forEach((entry) => {
        const traveler_index = entry[0];
        const itineraries = Object.entries(entry[1]);
        itineraries.forEach((itinerary) => {
          const itinerary_index = itinerary[0];
          const bags = Object.entries(itinerary[1]);
          bags.forEach((elem) => {
            const bag_info = elem[1];

            if (bag_info.added) {
              const bag = bag_info.bag;
              const prev = mapped[`${bag.name}:${bag.quantity}`] || {
                itineraries: [],
                travelers: [],
              };
              mapped[`${bag.name}:${bag.quantity}`] = {
                ...bag,
                itineraries: !prev.itineraries.includes(itinerary_index)
                  ? [...prev.itineraries, itinerary_index]
                  : prev.itineraries,
                travelers: !prev.travelers.includes(traveler_index)
                  ? [...prev.travelers, traveler_index]
                  : prev.travelers,
              };
            }
          });
        });
      });

      return mapped;
    },
    traveler_details() {
      return this.$store.state.liveflight.traveler_details;
    },
  },
  created() {
    this.get_markup = get_markup;
    this.taxes = taxes;
    this.segmentsMap = getSegmentsFromFlightOffers(this.flight_offers);
  },
};
</script>

<style lang="scss" scoped>
.sidebar-resume {
  padding: 2 * $grid-gutter-width/3 $grid-gutter-width/6 $grid-gutter-width/6
    $grid-gutter-width/6;

  &--contained {
    background: var(--white);
    padding: 2 * $grid-gutter-width/3;
    border-radius: $border-radius * 2;
    margin-bottom: 2 * $grid-gutter-width/3;
  }

  p,
  .paragraph {
    font-size: 12px;
    margin-top: 10px;
    @include font-primary-regular;
  }

  &__blocks {
    display: flex;
    flex-direction: column;
    gap: 30px;
  }
}
</style>
